import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Logo } from "@/components/landing/Reveal";

const DOCS = {
    terms: {
        title: "Terms & Conditions",
        updated: "Last updated: September 2026",
        sections: [
            {
                h: "1. What ITF is — and what it is not",
                p: [
                    "Institutional Trend Flow (“ITF”, “the Product”) is a software-based technical analysis indicator that runs on the TradingView® charting platform. ITF processes publicly available market data through proprietary metrics and displays directional market conditions visually on a chart, in the form of a colored trend line and related visual elements. ITF is designed for intraday analysis on the 5-minute timeframe, on liquid, actively traded markets.",
                    "ITF is an informational and educational software tool only. ITF does not provide investment advice, trading advice, financial advice, portfolio management, or recommendations of any kind, and nothing displayed by the Product, on this website, in our emails, or in any of our communications should be interpreted as such. ITF is not a signal service, does not execute trades, and does not guarantee any outcome or performance. All trading decisions you make are entirely your own.",
                    "Trading and investing involve substantial risk of loss and are not suitable for every person. Past performance, historical chart examples, and simulated demonstrations are not indicative of future results. You should never trade with money you cannot afford to lose, and you should consult a licensed financial advisor before making investment decisions.",
                ],
            },
            {
                h: "2. Orders, payment and our Merchant of Record",
                p: [
                    "All orders placed through this website are processed by Dodo Payments, our authorized reseller and Merchant of Record. Dodo Payments handles payment processing, the calculation and collection of applicable taxes, invoicing, receipts and payment-related customer support. Your card or bank statement will show the charge as a Dodo Payments transaction.",
                    "We never see, store or process your full payment card details. By completing a purchase you also agree to Dodo Payments’ own terms and privacy policy as presented at checkout.",
                    "The price of the Product is $275 USD for twelve (12) months of access. This is a one-time payment. There is no automatic renewal and no recurring charge; when your access period ends, you may purchase again if you wish to continue.",
                ],
            },
            {
                h: "3. Access and delivery",
                p: [
                    "Access to the Product is delivered digitally by granting your TradingView username access to our invite-only script. You must provide a valid, correct TradingView username at checkout. We verify each order and activate access manually — usually within a few hours, and always within twenty-four (24) hours of payment confirmation.",
                    "Access is personal, limited to the single TradingView account you register at checkout, and is non-transferable. We will never ask you for your TradingView password or any TradingView login credentials, and you should never share them with anyone claiming to represent us.",
                    "If you provide an incorrect TradingView username, contact us at support@getitf.xyz and we will transfer the access grant to the corrected username after verification.",
                ],
            },
            {
                h: "4. Acceptable use",
                p: [
                    "You may not copy, reproduce, modify, reverse-engineer, decompile, redistribute, resell, sublicense, publish or share the Product, your access to it, or any part of its code or logic. You may not share your TradingView account or otherwise allow third parties to use your access.",
                    "Any violation of these terms, any abuse, any fraudulent activity, or any chargeback or payment dispute filed in bad faith will result in immediate revocation of your access without refund, and may result in legal action where applicable.",
                ],
            },
            {
                h: "5. No guarantees; limitation of liability",
                p: [
                    "The Product is provided “as is” and “as available”, without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, accuracy, reliability or non-infringement. We do not warrant that the Product will be uninterrupted, error-free or compatible with future changes to the TradingView platform, although we will make commercially reasonable efforts to maintain compatibility.",
                    "To the maximum extent permitted by applicable law, Institutional Trend Flow, its owners, operators and affiliates will not be liable for any direct, indirect, incidental, consequential, special or exemplary damages — including but not limited to trading losses, lost profits, lost data, or loss of opportunity — arising out of or related to the use of, or inability to use, the Product. In all cases, our total aggregate liability to you is limited to the amount you paid for the Product ($275 USD).",
                ],
            },
            {
                h: "6. Intellectual property and third-party trademarks",
                p: [
                    "The Product, this website, the ITF name and brand, and all associated code, text, graphics and materials are the property of Institutional Trend Flow and are protected by applicable intellectual property laws.",
                    "TradingView® is a registered trademark of TradingView, Inc. TradingView has no affiliation with Institutional Trend Flow, and Institutional Trend Flow is not sponsored, approved or endorsed by TradingView, Inc. All other trademarks belong to their respective owners.",
                ],
            },
            {
                h: "7. Refunds",
                p: [
                    "All sales are final and no refunds are given in any case, to the maximum extent permitted by applicable law. Please read our Refund Policy in full before purchasing.",
                ],
            },
            {
                h: "8. Changes and contact",
                p: [
                    "We may update these Terms & Conditions from time to time. The version published on this page at the time of your purchase governs that purchase. Continued use of the Product after an update constitutes acceptance of the updated terms.",
                    "For any question about these terms, contact us at support@getitf.xyz.",
                ],
            },
        ],
    },
    refund: {
        title: "Refund Policy",
        updated: "Last updated: September 2026",
        sections: [
            {
                h: "All sales are final",
                p: [
                    "ITF is a digital product delivered by granting access to your personal TradingView account. Because digital access cannot be returned once granted, all sales are final and no refunds, returns, exchanges or credits are given in any case, to the maximum extent permitted by applicable law. This applies regardless of usage, results, market conditions, or changes in your personal circumstances.",
                    "By completing your purchase you expressly acknowledge and agree that you lose any right to withdraw from the purchase once digital delivery (activation of your TradingView access) has begun, and you accept this strict no-refund policy in full.",
                ],
            },
            {
                h: "If something goes wrong",
                p: [
                    "A strict no-refund policy does not mean you are on your own. If your access cannot be activated because you entered an incorrect TradingView username, we will transfer the grant to the correct username at no cost. If the Product fails to load or function as described, contact us at support@getitf.xyz and we will investigate and restore functionality as our sole remedy.",
                    "Our remedy for any verified technical problem is repair, replacement or re-activation of access — not a refund.",
                ],
            },
            {
                h: "Chargebacks and payment disputes",
                p: [
                    "If you file a chargeback or payment dispute instead of contacting us first, your access will be revoked immediately and permanently. We will contest the dispute with evidence of your order, your acceptance of this policy, and the delivery of digital access to your TradingView account.",
                    "Payment-related questions can also be directed to Dodo Payments (our Merchant of Record) via the receipt you receive at checkout.",
                ],
            },
            {
                h: "Your statutory rights",
                p: [
                    "Nothing in this policy limits or excludes any consumer rights you may have under mandatory applicable law that cannot lawfully be waived. Where such rights apply, they prevail over this policy to the minimum extent required.",
                ],
            },
            {
                h: "Contact",
                p: [
                    "For any question about this policy, contact us at support@getitf.xyz before or after your purchase — we are happy to help you decide whether ITF is right for you before you buy.",
                ],
            },
        ],
    },
    privacy: {
        title: "Privacy Policy",
        updated: "Last updated: September 2026",
        sections: [
            {
                h: "1. Who we are",
                p: [
                    "Institutional Trend Flow (“we”, “us”) operates this website and sells the ITF indicator for the TradingView platform. You can reach us at any time at support@getitf.xyz.",
                ],
            },
            {
                h: "2. What we collect",
                p: [
                    "When you purchase, we collect: your full name, your email address, and your TradingView username. We also keep records of your order (order ID, date, plan, price, access status and activation history).",
                    "Payment card details are processed entirely by Dodo Payments, our Merchant of Record. We never see, store or have access to your full card number or payment credentials.",
                    "This website uses only essential, privacy-respecting technical storage required for the site and checkout to function. We do not use advertising trackers.",
                ],
            },
            {
                h: "3. How we use your data",
                p: [
                    "We use your data to: process and fulfill your order; grant, manage and if necessary revoke your indicator access; send you transactional emails about your order (confirmation, activation, support replies); prevent fraud and abuse; and meet legal and accounting obligations.",
                    "We do not sell your data, rent it, or share it with advertisers. We never ask for or store your TradingView password.",
                ],
            },
            {
                h: "4. Who we share it with",
                p: [
                    "Dodo Payments (Merchant of Record) processes your payment and related tax and invoicing data under its own privacy policy. TradingView, Inc. receives only your TradingView username, solely so we can grant your script access on their platform. Our email service provider processes your email address solely to deliver transactional order emails.",
                ],
            },
            {
                h: "5. Retention, security and your rights",
                p: [
                    "We keep order records for as long as needed to provide the service and to satisfy legal, tax and accounting obligations. We apply industry-standard technical and organizational measures to protect your data.",
                    "Depending on your jurisdiction, you may have the right to access, correct, export or delete your personal data, or to object to certain processing. To exercise any of these rights, email support@getitf.xyz and we will respond within a reasonable timeframe.",
                ],
            },
            {
                h: "6. Changes",
                p: [
                    "We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated revision date.",
                ],
            },
        ],
    },
};

const TABS = [
    { key: "terms", label: "Terms & Conditions", to: "/terms" },
    { key: "refund", label: "Refund Policy", to: "/refund-policy" },
    { key: "privacy", label: "Privacy Policy", to: "/privacy" },
];

export default function Legal({ doc }) {
    const d = DOCS[doc] || DOCS.terms;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [doc]);

    return (
        <div className="min-h-screen bg-[#FAFBFD]" data-testid={`legal-${doc}-page`}>
            <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" data-testid="legal-logo-link">
                        <Logo />
                    </Link>
                    <Link to="/" data-testid="legal-back-link" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900">
                        <ArrowLeft className="h-4 w-4" /> Back to site
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
                <nav className="flex flex-wrap gap-2" data-testid="legal-tabs">
                    {TABS.map((t) => (
                        <Link
                            key={t.key}
                            to={t.to}
                            data-testid={`legal-tab-${t.key}`}
                            className={`rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                                t.key === doc ? "bg-slate-900 text-white shadow-md" : "border border-slate-200 bg-white text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            {t.label}
                        </Link>
                    ))}
                </nav>

                <h1 className="mt-10 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{d.title}</h1>
                <p className="mt-2 font-mono text-xs text-slate-400">{d.updated}</p>

                <div className="mt-10 space-y-10">
                    {d.sections.map((s) => (
                        <section key={s.h}>
                            <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{s.h}</h2>
                            <div className="mt-3 space-y-3">
                                {s.p.map((para, i) => (
                                    <p key={i} className="text-sm leading-relaxed text-slate-600">
                                        {para}
                                    </p>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-12 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
                    <Mail className="h-4 w-4 shrink-0 text-emerald-600" />
                    <p>
                        Questions about this document? Contact us at{" "}
                        <a href="mailto:support@getitf.xyz" className="font-semibold text-emerald-700 hover:text-emerald-800">
                            support@getitf.xyz
                        </a>
                        .
                    </p>
                </div>
            </main>
        </div>
    );
}
